// @flow
import config from '../../config';
import { makeAPICall, APIError, redirectTo } from '../generalCreators';
import {
  updateLocations,
  updateCurrentLocation
} from '../locations/locationCreators';
import { updateCurrentCustomer } from '../customers/currentCustomerCreators';
import { updateCurrentOrganisation } from './organisationCreators';
import type { ThunkAction } from '../../types';

export function getOrganisations(
  organisationId: ?number = undefined,
  withPermissions: ?boolean = true,
  excludeArchived: ?boolean = true
): ThunkAction {
  return function(dispatch, getState) {
    const endpoint = '/organisations/access';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        withPermissions,
        excludeArchived
      }
    };

    // set organisationId if it was passed in
    if (organisationId) {
      // $FlowFixMe
      options.params.organisationId = organisationId;
    }
    return makeAPICall(endpoint, options).then(
      response => {
        // 'organisations' could be either an 'Organisation' or a 'Location'
        const organisations = response.data.organisations;
        if (organisationId) {
          // if getting sub-organisations
          dispatch(updateLocations(organisations));
          dispatch(updateCurrentLocation());
        } else {
          // if getting top level organisations
          dispatch(updateOrganisations(organisations));
          if (organisations.length === 1) {
            dispatch(updateCurrentOrganisation(organisations[0].id));
          }
          const user = getState().authentication.user;
          if (
            organisations.length === 0 &&
            user.isEmailVerified &&
            user.isMobileVerified
          ) {
            return redirectTo('/customer/create');
          }
        }
      },
      error => dispatch(APIError(error, 'Error getting organisations'))
    );
  };
}

export const UPDATE_ORGANISATIONS = 'UPDATE_ORGANISATIONS';
export function updateOrganisations(organisations: Object[]) {
  return {
    type: UPDATE_ORGANISATIONS,
    organisations
  };
}

export function getOrganisation(
  organisationId: number,
  withSettings: ?boolean = true
): ThunkAction {
  return function(dispatch, getState) {
    const endpoint = '/organisation';
    const options = {
      method: 'GET',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        withSettings
      }
    };

    return makeAPICall(endpoint, options).then(
      response => {
        return {
          ...response.data.organisation,
          settings: response.data.settings
        };
      },
      error => dispatch(APIError(error, 'Error getting organisation'))
    );
  };
}

export function addOrganisation(organisation: Object): ThunkAction {
  return function(dispatch, getState) {
    const currentOrganisationId = getState().currentOrganisation.id;
    const endpoint = '/organisations';
    const options = {
      method: 'POST',
      headers: { token: getState().authentication.token },
      params: { productId: config.productId },
      data: organisation
    };
    return makeAPICall(endpoint, options).then(
      response => {
        const newOrganisationId = response.data.id;
        dispatch(getOrganisations(currentOrganisationId));
        dispatch(updateCurrentCustomer(newOrganisationId));
        return response;
      },
      error => dispatch(APIError(error, 'Error creating organisation'))
    );
  };
}

export function updateOrganisation(organisation: Object): ThunkAction {
  return function(dispatch, getState) {
    const endpoint = '/organisation';
    const options = {
      method: 'PUT',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId: organisation.id
      },
      data: organisation
    };
    return makeAPICall(endpoint, options).then(
      response => {
        dispatch(getOrganisations(organisation.organisationOwnerId));
        dispatch(updateCurrentCustomer(organisation.id));
        return response;
      },
      error => dispatch(APIError(error, 'Error updating organisation'))
    );
  };
}

export function registerOrganisation(values: Object): ThunkAction {
  const {
    name,
    industryId,
    phone,
    address,
    abn,
    acn,
    organisationTypeId,
    canSendToNoJunkMail = false,
    canSendToDoNotMail = false,
    agencyRequest
  } = values;

  return function(dispatch, getState) {
    const endpoint = '/account/register/organisation';
    const options = {
      method: 'POST',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        setAllPermissions: true
      },
      data: {
        name,
        industryId,
        phone,
        address,
        abn,
        acn,
        organisationTypeId,
        canSendToNoJunkMail,
        canSendToDoNotMail
      }
    };
    return makeAPICall(endpoint, options).then(
      response => {
        if (agencyRequest) {
          // TODO: Remove hardcoded agency ID.
          dispatch(requestOrganisationTypeUpdate(response.data.id, 3));
        }
        return response;
      },
      error => dispatch(APIError(error, 'Error registering organisation'))
    );
  };
}

export function requestOrganisationTypeUpdate(
  organisationId: number,
  requestedOrganisationTypeId: number
): ThunkAction {
  return function(dispatch, getState) {
    const endpoint = '/organisation/requestOrganisationTypeUpdate';
    const options = {
      method: 'POST',
      headers: { token: getState().authentication.token },
      params: {
        productId: config.productId,
        organisationId,
        requestedOrganisationTypeId
      },
      data: {} // An empty data not used by the API. POST request needs data to correctly set ContentType to be application/json
    };
    return makeAPICall(endpoint, options).then(
      response => {
        return response;
      },
      error => dispatch(APIError(error, 'Error updating organisation type'))
    );
  };
}



// WEBPACK FOOTER //
// ./src/actions/organisations/basicCreators.js